export type SessionSnapshotBundleInput = {
  outlineSnapshotId?: string | null;
  outlineId?: string | null;
  characterSnapshots?: Record<string, string>;
  worldbuildingSnapshots?: Record<string, string>;
  metaSnapshot?: unknown;
  createdCharacterIds?: string[];
  createdWorldbuildingIds?: string[];
};

export type SessionSnapshotBundle = {
  outlineSnapshotId: string | null;
  outlineId: string | null;
  characterSnapshots: Record<string, string>;
  worldbuildingSnapshots: Record<string, string>;
  metaSnapshot: unknown;
  createdCharacterIds: string[];
  createdWorldbuildingIds: string[];
};

export type HistoryMessageWithSnapshotBundle = {
  role?: string;
  created_at?: Date | string | null;
  metadata?: {
    snapshotBundle?: SessionSnapshotBundleInput | null;
    triggerUserMessageId?: string;
  } | null;
};

function messageTimeMs(message: HistoryMessageWithSnapshotBundle): number {
  const value = message.created_at;
  if (!value) return Number.NaN;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : Number.NaN;
}

function emptySessionSnapshotBundle(): SessionSnapshotBundle {
  return {
    outlineSnapshotId: null,
    outlineId: null,
    characterSnapshots: {},
    worldbuildingSnapshots: {},
    metaSnapshot: null,
    createdCharacterIds: [],
    createdWorldbuildingIds: [],
  };
}

function recordMapsEqual(a: Record<string, string>, b: Record<string, string>): boolean {
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key, index) => key === bKeys[index] && a[key] === b[key]);
}

function stringArraysEqualAsSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const bSet = new Set(b);
  return a.every((id) => bSet.has(id));
}

function metaSnapshotsEqual(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (typeof a !== "object" || typeof b !== "object") return a === b;
  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(aObj), ...Object.keys(bObj)]);
  for (const key of keys) {
    if (aObj[key] !== bObj[key]) return false;
  }
  return true;
}

/**
 * Earliest per-entity snapshots captured in this chat (first assistant turn that mutated each entity).
 * Used to restore Story Bible state from before the agent's edits in the session, not just the last turn.
 */
export function buildSessionBaselineFromHistory(
  history: HistoryMessageWithSnapshotBundle[],
): SessionSnapshotBundle | null {
  const baseline = emptySessionSnapshotBundle();
  let sawAny = false;

  const assistantMessages = history
    .map((message, index) => ({ message, index }))
    .filter(({ message }) => message.role === "assistant")
    .sort((a, b) => {
      const aMs = messageTimeMs(a.message);
      const bMs = messageTimeMs(b.message);
      const aKey = Number.isFinite(aMs) ? aMs : a.index;
      const bKey = Number.isFinite(bMs) ? bMs : b.index;
      if (aKey !== bKey) return aKey - bKey;
      return a.index - b.index;
    });

  for (const { message } of assistantMessages) {
    const bundle = message.metadata?.snapshotBundle;
    if (!bundle) continue;

    if (bundle.outlineSnapshotId && bundle.outlineId && !baseline.outlineSnapshotId) {
      baseline.outlineSnapshotId = bundle.outlineSnapshotId;
      baseline.outlineId = bundle.outlineId;
      sawAny = true;
    }

    for (const [id, snapId] of Object.entries(bundle.characterSnapshots ?? {})) {
      if (snapId && !baseline.characterSnapshots[id]) {
        baseline.characterSnapshots[id] = snapId;
        sawAny = true;
      }
    }

    for (const [id, snapId] of Object.entries(bundle.worldbuildingSnapshots ?? {})) {
      if (snapId && !baseline.worldbuildingSnapshots[id]) {
        baseline.worldbuildingSnapshots[id] = snapId;
        sawAny = true;
      }
    }

    if (bundle.metaSnapshot != null && baseline.metaSnapshot == null) {
      baseline.metaSnapshot = bundle.metaSnapshot;
      sawAny = true;
    }

    for (const id of bundle.createdCharacterIds ?? []) {
      if (id && !baseline.createdCharacterIds.includes(id)) {
        baseline.createdCharacterIds.push(id);
        sawAny = true;
      }
    }

    for (const id of bundle.createdWorldbuildingIds ?? []) {
      if (id && !baseline.createdWorldbuildingIds.includes(id)) {
        baseline.createdWorldbuildingIds.push(id);
        sawAny = true;
      }
    }
  }

  return sawAny ? baseline : null;
}

export function sessionBaselineHasRestorableSnapshots(
  bundle: SessionSnapshotBundle | SessionSnapshotBundleInput | null | undefined,
): boolean {
  if (!bundle) return false;
  return (
    Boolean(bundle.outlineSnapshotId && bundle.outlineId)
    || Object.keys(bundle.characterSnapshots ?? {}).length > 0
    || Object.keys(bundle.worldbuildingSnapshots ?? {}).length > 0
    || bundle.metaSnapshot != null
    || (bundle.createdCharacterIds?.length ?? 0) > 0
    || (bundle.createdWorldbuildingIds?.length ?? 0) > 0
  );
}

/** User message id to trim from when restoring the full session baseline (earliest Story Bible edit turn). */
export function findSessionBaselineTrimUserMessageId(
  history: HistoryMessageWithSnapshotBundle[],
): string | null {
  let earliest: { id: string; sortKey: number; index: number } | null = null;

  for (let index = 0; index < history.length; index++) {
    const message = history[index];
    if (message.role !== "assistant") continue;
    const bundle = message.metadata?.snapshotBundle;
    if (!sessionBaselineHasRestorableSnapshots(bundle)) continue;
    const triggerId = message.metadata?.triggerUserMessageId;
    if (typeof triggerId !== "string" || !triggerId) continue;

    const ms = messageTimeMs(message);
    const sortKey = Number.isFinite(ms) ? ms : index;
    if (
      !earliest
      || sortKey < earliest.sortKey
      || (sortKey === earliest.sortKey && index < earliest.index)
    ) {
      earliest = { id: triggerId, sortKey, index };
    }
  }

  return earliest ? earliest.id : null;
}

/** Order-independent structural compare for session vs turn snapshot bundles. */
export function sessionBaselineDiffersFromTurnBundle(
  sessionBaseline: SessionSnapshotBundle | SessionSnapshotBundleInput,
  turnBundle: SessionSnapshotBundle | SessionSnapshotBundleInput,
): boolean {
  if (sessionBaseline.outlineSnapshotId !== turnBundle.outlineSnapshotId) return true;
  if (sessionBaseline.outlineId !== turnBundle.outlineId) return true;
  if (!recordMapsEqual(sessionBaseline.characterSnapshots ?? {}, turnBundle.characterSnapshots ?? {})) {
    return true;
  }
  if (!recordMapsEqual(sessionBaseline.worldbuildingSnapshots ?? {}, turnBundle.worldbuildingSnapshots ?? {})) {
    return true;
  }
  if (!metaSnapshotsEqual(sessionBaseline.metaSnapshot, turnBundle.metaSnapshot)) return true;
  if (!stringArraysEqualAsSet(sessionBaseline.createdCharacterIds ?? [], turnBundle.createdCharacterIds ?? [])) {
    return true;
  }
  if (!stringArraysEqualAsSet(sessionBaseline.createdWorldbuildingIds ?? [], turnBundle.createdWorldbuildingIds ?? [])) {
    return true;
  }
  return false;
}
